/**
 * VehicleUtilities: CSV loading and customer/metafield/localStorage vehicle state.
 * CSV loaders (fetchCsvAsJson / tsv*JSON) are the single place that populate window.globalVariables.*Json.
 */
class VehicleUtilities {

    /**
     * Generic CSV-to-JSON loader. Fetches CSV from globalVariables[urlKey], parses with CSVToArray,
     * maps each row via rowMapper, assigns result to window.globalVariables[globalKey], and returns it.
     * @param {string} urlKey - Key in window.globalVariables for the CSV URL (e.g. 'vehicleCsv')
     * @param {function} rowMapper - function(data, key) returns object for that row
     * @param {string} globalKey - Key in window.globalVariables for the result (e.g. 'vehicleJson')
     * @returns {Promise<object>} The parsed JSON object
     */
    async fetchCsvAsJson(urlKey, rowMapper, globalKey) {
        const url = window.globalVariables?.[urlKey];
        if (!url) {
            if (window.globalVariables) window.globalVariables[globalKey] = {};
            return {};
        }
        const response = await fetch(url);
        if (!response.ok) {
            if (window.globalVariables) window.globalVariables[globalKey] = {};
            return {};
        }
        try {
            const csvdata = await response.text();
            const fileData = vehicleUtility.CSVToArray(csvdata);
            const arr = {};
            fileData.forEach((data, key) => {
                arr[String(key)] = rowMapper(data, key);
            });
            const json = JSON.stringify(arr);
            const result = JSON.parse(json);
            window.globalVariables[globalKey] = result;
            return result;
        } catch (e) {
            console.error('CSV load failed:', e);
            if (window.globalVariables) window.globalVariables[globalKey] = {};
            return {};
        }
    }

    /**
     * @returns - vehicle JSON; accept the csvFile from the metaobject and converting into json
     */
    async vehicleCSVJSON() {
        return this.fetchCsvAsJson('vehicleCsv', (data) => ({
            year: `${data['Year']}` || null,
            make: `${data['Make']}` || null,
            model: `${data['Model']}` || null,
            trim: `${data['Trim']}` || null,
            cab_type: data['Cab Type'] || null,
            row: `${data['Row']}` || null,
            part_number: `${data['Part Number']}` || null
        }), 'vehicleJson');
    }

    /**
     * @returns - heavyequipment JSON; accept the csvFile from the metaobject and converting into json
     */
    async heavyEquipmentCSVJSON() {
        return this.fetchCsvAsJson('heavyvehicleCsv', (data) => {
            const equipmentType =
                data['Type'] ||
                data['Equipment Type'] ||
                data['Equipment type'] ||
                data['Category'] ||
                data['Equipment Category'] ||
                data['Equipment category'] ||
                data['equipment_category'] ||
                null;
            return {
                equipment_category: equipmentType,
                equipment_model_number: data['Model Number'] || data['Model number'] || null,
                equipment_part_number: data['Part Number'] || data['Part number'] || null,
                equipment_manufacturer: data['Manufacturer'] || data['Make'] || null
            };
        }, 'heavyvehicleJson');
    }

    /**
     * @returns - side by side JSON; accept the csvFile from the metaobject and converting into json
     */
    async SideBySideCSVJSON() {
        return this.fetchCsvAsJson('sidevehicleCsv', (data) => ({
            year: `${data['Year ']}` || null,
            make: `${data['Make']}` || null,
            model: `${data['Model']}` || null,
            part_number: `${data['Part Number']}` || null,
            row: `${data['Row']}` || null
        }), 'sidevehicleJson');
    }

    /**
     * @returns - Agri and turf JSON; accept the csvFile from the metaobject and converting into json
     */
    async AgricultureTurfCSVJSON() {
        return this.fetchCsvAsJson('agriTurfVehicleCsv', (data) => ({
            equipment_category: `${data['Type']}` || null,
            equipment_manufacturer: `${data['Manufacturer']}` || null,
            equipment_model_number: `${data['Model Number']}` || null,
            equipment_part_number: `${data['Part Number']}` || null
        }), 'agriTurfVehicleJson');
    }

    /**
     * @returns - semi and heavy duty trucks JSON
     */
    async SemiAndHeavyTrucksCSVJSON() {
        return this.fetchCsvAsJson('semiAndHeavyTrucksCSV', (data) => ({
            year: `${data['Year']}` || null,
            make: `${data['Make']}` || null,
            model: `${data['Model']}` || null,
            part_number: `${data['Part Number']}` || null
        }), 'sahdtJson');
    }

    /**
     * getOrLoad: check window.globalVariables[globalKey] first; if data present return it, else load CSV.
     * In-flight promise per key avoids duplicate fetches. Use type or the public getOrLoad* wrappers.
     * @param {string} type - 'vehicle' | 'heavy-equipment' | 'side-by-side' | 'agri-turf' | 'semis-heavy-duty-trucks'
     * @returns {Promise<object>}
     */
    getOrLoad(type) {
        const normalized = (type || '').toLowerCase().replace(/\s+/g, '-');
        let key, loader;
        switch (normalized) {
            case 'vehicle':
                key = 'vehicleJson';
                loader = () => this.vehicleCSVJSON();
                break;
            case 'heavy-equipment':
                key = 'heavyvehicleJson';
                loader = () => this.heavyEquipmentCSVJSON();
                break;
            case 'side-by-side':
                key = 'sidevehicleJson';
                loader = () => this.SideBySideCSVJSON();
                break;
            case 'agri-turf':
            case 'agriculture-turf':
                key = 'agriTurfVehicleJson';
                loader = () => this.AgricultureTurfCSVJSON();
                break;
            case 'semis-heavy-duty-trucks':
                key = 'sahdtJson';
                loader = () => this.SemiAndHeavyTrucksCSVJSON();
                break;
            default:
                return Promise.resolve({});
        }
        const existing = window.globalVariables?.[key];
        if (existing && typeof existing === 'object' && Object.keys(existing).length > 0) return Promise.resolve(existing);
        this._loadPromises = this._loadPromises || {};
        if (!this._loadPromises[key]) this._loadPromises[key] = loader();
        return this._loadPromises[key];
    }

    /**
     * Map data-vehicle-type (or template suffix) to the corresponding getOrLoad promise.
     * @param {string} vehicleType - e.g. 'vehicle', 'side-by-side', 'semis-heavy-duty-trucks', 'heavy-equipment', 'agriculture-turf'
     * @returns {Promise<object>}
     */
    getOrLoadByType(vehicleType) {
        return this.getOrLoad(vehicleType);
    }

    /**
     * Run callback once when element is visible. Uses IntersectionObserver (threshold 0.01); fallback setTimeout.
     * @param {Element} element - DOM element to observe
     * @param {function(): void} callback - Called once when element becomes visible
     */
    whenVisible(element, callback) {
        if (!element || typeof callback !== 'function') return;
        if (typeof IntersectionObserver !== 'undefined') {
            const io = new IntersectionObserver((entries) => {
                if (!entries[0].isIntersecting) return;
                io.disconnect();
                callback();
            }, { threshold: 0.01 });
            io.observe(element);
        } else {
            setTimeout(callback, 0);
        }
    }

    /**
     * Data transformation: from vehicle JSON (make/model keys) to unique "Make | Model" list.
     * Used by model-vehicle, model-side-by-side, model-semis-heavy-duty-trucks for consistent behavior.
     * @param {Object} json - Keyed object of { make, model, ... }
     * @returns {string[]} Unique "Make | Model" strings
     */
    getUniqueMakeModelsFromData(json) {
        const arr = (Object.values(json || {})).filter(item => item != null && typeof item === 'object');
        const combined = arr.map(item => `${item.make || ''} | ${item.model || ''}`.trim()).filter(Boolean);
        return [...new Set(combined)];
    }

    /**
     * Data transformation: from equipment JSON to unique category list.
     * @param {Object} json - Keyed object with equipment_category (or Type) field
     * @param {string} [categoryKey] - Key for category (default 'equipment_category')
     * @returns {string[]} Unique category strings
     */
    getUniqueCategoriesFromData(json, categoryKey = 'equipment_category') {
        const arr = (Object.values(json || {})).filter(item => item != null && typeof item === 'object');
        const categories = arr.map(item => item[categoryKey] || item.Type).filter(Boolean);
        return [...new Set(categories)];
    }

    /**
     * Normalize search input: lowercase, collapse whitespace, trim.
     * Used by filterListByQuery, getCategoryMatches, and model handleSearch.
     */
    normalizeQuery(value) {
        return String(value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    }

    /** NaN-safe descending numeric sort for years/lists. */
    _sortDescendingNumeric(a, b) {
        return (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0);
    }

    /**
     * Centralized comparison: filter a list by normalized query (includes match).
     * Used by all model files for make/model, year, trim, cabType, manufacturer, model number.
     * @param {Array} list - Array of values to filter (strings or items to normalize)
     * @param {string} query - Raw search input
     * @param {Object} [options] - Optional: normalizer(item), sortDescending (e.g. for years)
     * @returns {Array} Filtered (and optionally sorted) list
     */
    filterListByQuery(list, query, options = {}) {
        const normalizedQuery = this.normalizeQuery(query);
        if (!normalizedQuery) return [];
        const normalizer = options.normalizer || (item => String(item ?? '').toLowerCase().trim());
        let result = (list || []).filter(item => {
            try {
                return normalizer(item).includes(normalizedQuery);
            } catch (_) {
                return false;
            }
        });
        if (options.sortDescending) {
            result = result.sort((a, b) => this._sortDescendingNumeric(a, b));
        }
        return result;
    }

    /**
     * Default normalizer for "Make | Model" style strings (e.g. "Ford | F-150").
     * Use with filterListByQuery for makeModel type.
     */
    normalizerMakeModel(item) {
        return String(item ?? '').toLowerCase().replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
    }

    /**
     * Category-style search: match by model number first, then include category name matches (combined and deduped).
     * Shared by heavy-equipment and agriculture-turf. Return shape: { displayText, isModelMatch, category, manufacturer, model }.
     * @param {Array} vehicleArray - Object.values(json) of equipment data
     * @param {Array} uniqueCategories - Unique category labels
     * @param {string} query - Raw search input
     * @param {Object} keys - Field names: { categoryKey, manufacturerKey, modelKey }
     * @param {Object} [opts] - Optional: { combineResults: true } to return both model and category matches
     * @returns {Array<{displayText, isModelMatch, category, manufacturer, model}>}
     */
    getCategoryMatches(vehicleArray, uniqueCategories, query, keys = {}, opts = {}) {
        const cKey = keys.categoryKey || 'equipment_category';
        const mfrKey = keys.manufacturerKey || 'equipment_manufacturer';
        const modelKey = keys.modelKey || 'equipment_model_number';
        const normalizedQuery = this.normalizeQuery(query);
        if (!normalizedQuery) return [];

        const arr = (vehicleArray || []).filter(item => item != null && typeof item === 'object');
        const modelMatches = arr.filter(item =>
            item[modelKey] && String(item[modelKey]).toLowerCase().includes(normalizedQuery)
        ).map(item => ({
            displayText: `${item[cKey]} ${item[mfrKey]} ${item[modelKey]}`.replace(/\s+/g, ' ').trim(),
            isModelMatch: true,
            category: item[cKey] || '',
            manufacturer: item[mfrKey] || '',
            model: item[modelKey] || ''
        }));

        const categoryMatches = (uniqueCategories || [])
            .filter(cat => String(cat).toLowerCase().includes(normalizedQuery))
            .map(cat => ({
                displayText: String(cat),
                isModelMatch: false,
                category: cat,
                manufacturer: '',
                model: ''
            }));

        if (opts.combineResults && modelMatches.length > 0) {
            const seen = new Set(modelMatches.map(m => m.displayText));
            const extra = categoryMatches.filter(c => !seen.has(c.displayText));
            return [...modelMatches, ...extra];
        }
        if (modelMatches.length > 0) return modelMatches;
        return categoryMatches;
    }

    /**
     * From data (array-like of { make, model, year }), return unique years for given make/model, newest first.
     * Used by model-vehicle, model-side-by-side, model-semis-heavy-duty-trucks.
     */
    getUniqueYearsFromData(data, make, model) {
        const arr = (Object.values(data || {})).filter(v => v != null && typeof v === 'object');
        const matchingYears = arr
            .filter(v => v.make === make && v.model === model)
            .map(v => v.year)
            .filter(Boolean);
        return [...new Set(matchingYears)].sort((a, b) => this._sortDescendingNumeric(a, b));
    }

    /**
     * Filter vehicle/equipment array by criteria and return unique values for one field.
     * Replaces getMatchingOptions (vehicle, side-by-side, semis) and getUniqueOptions (heavy-equipment, agriculture-turf).
     * @param {Array} vehicleArray - Object.values(json)
     * @param {string} field - Field to extract (e.g. 'year', 'trim', 'cab_type', 'equipment_manufacturer')
     * @param {Object} filters - Key/value criteria; row kept only if it matches all entries
     * @param {Object} [options] - fieldMap (field name -> data key), allowEmptyFilters, valueGetter(key, row), compareCaseInsensitive, sortDescending
     * @returns {Array} Unique values for that field
     */
    getFilteredUniqueValues(vehicleArray, field, filters, options = {}) {
        const fieldMap = options.fieldMap || {};
        const actualField = fieldMap[field] || field;
        const allowEmpty = options.allowEmptyFilters === true;
        const valueGetter = options.valueGetter || ((key, row) => row[key]);
        const caseInsensitive = options.compareCaseInsensitive === true;
        const safeFilters = filters != null && typeof filters === 'object' ? filters : {};
        const arr = (vehicleArray || []).filter(row => row != null && typeof row === 'object');

        const matching = arr
            .filter(row => {
                try {
                    return Object.entries(safeFilters).every(([key, value]) => {
                    if (allowEmpty && (value === '' || value == null)) return true;
                    const rowVal = valueGetter(key, row);
                    if (caseInsensitive) {
                        return String(rowVal || '').toLowerCase() === String(value || '').toLowerCase();
                    }
                    return rowVal === value;
                });
                } catch (_) {
                    return false;
                }
            })
            .map(row => row[actualField])
            .filter(val => val != null && String(val).trim() !== '');

        let unique = [...new Set(matching)];
        if (options.sortDescending) {
            unique = unique.sort((a, b) => this._sortDescendingNumeric(a, b));
        }
        return unique;
    }

    /**
     * Get comma-separated part number tags (sku-xxx) for rows matching the selection.
     * Replaces getPartNumbers in all 5 model files.
     * @param {Array} vehicleArray - Object.values(json)
     * @param {Object} selection - Criteria (e.g. { year, make, model } or { equipment_category, equipment_manufacturer, equipment_model_number })
     * @param {Object} [options] - partNumberKey ('part_number' | 'equipment_part_number'), compareCaseInsensitive (default true)
     * @returns {string} e.g. 'sku-1,sku-2'
     */
    getPartNumbersForSelection(vehicleArray, selection, options = {}) {
        const partKey = options.partNumberKey || 'part_number';
        const caseInsensitive = options.compareCaseInsensitive !== false;
        const safeSelection = selection != null && typeof selection === 'object' ? selection : {};
        const arr = (vehicleArray || []).filter(row => row != null && typeof row === 'object');

        const filtered = arr.filter(row =>
            Object.entries(safeSelection).every(([key, value]) => {
                if (value === '' || value == null) return true;
                const rowVal = row[key];
                if (caseInsensitive) {
                    return String(rowVal || '').toLowerCase() === String(value || '').toLowerCase();
                }
                return rowVal === value;
            })
        );

        const unique = [...new Set(filtered.map(item => item[partKey]).filter(Boolean))];
        return unique.map(p => `sku-${String(p).trim()}`).join(',');
    }

      /**
       * 
       * @param {*} csvData - CSV file
       * @param {*} delimiter 
       * convert csv to array
       * @returns - array 
       */
    CSVToArray(csvData, delimiter) {
        if (csvData == null || typeof csvData !== 'string') return [[]];
        delimiter = (delimiter || ",");
        var pattern = new RegExp((
        "(\\" + delimiter + "|\\r?\\n|\\r|^)" +
        "(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|" +
        "([^\"\\" + delimiter + "\\r\\n]*))"), "gi");
        var data = [[]];
        var matches = null;
        while (matches = pattern.exec(csvData)) {
            var matchedDelimiter = matches[1];
            if (matchedDelimiter.length && (matchedDelimiter != delimiter)) {
                data.push([]);
            }
            if (matches[2]) {
                var matchedDelimiter = matches[2].replace(
                new RegExp("\"\"", "g"), "\"");
            } else {
                var matchedDelimiter = matches[3];
            }
            data[data.length - 1].push(matchedDelimiter);
        }
        let jsonData = this.processData(data);
        return jsonData;
    }
    
    /**
       * 
       * @param {*} sheetData - csvsheet data
       * process csvsheet data
       * apply {key , value } pairs 
       * @returns 
    */
    processData(sheetData) {
        let data = [];
        let headers = [];
    
        sheetData.forEach((csvData, key) => {
        if (key === 0) {
            headers = csvData.map(header => header.replace('.', '_'));
            return;
        }
    
        let mappedData = {};
        csvData.forEach((item, csvKey) => {
            if (headers[csvKey]) {
            if (this.isJSON(item)) {
                mappedData[headers[csvKey]] = JSON.parse(item);
            } else {
                mappedData[headers[csvKey]] = item;
            }
            }
        });
    
        data.push(mappedData);
        });
    
        return data;
    }
    
      /**
       * 
       * @param {*} str 
       * @returns - true if json otherwise false
       */
      isJSON(str) {
          try {
          JSON.parse(str);
          return true;
          } catch (e) {
          return false;
          }
      }
  
      /**
       * determine the user vehicles
       */
      _deterMineVehicles(){
          const isCustomer = document.body.classList.contains('customer-loggedin');
          const isCustomerB2B = document.body.classList.contains('b2b-customer');
          
          this.selectedYMMData = [];
          this.selectedTrufData = [];
          this.selectedSideYMMData = [];
          this.selectedHeavyEqipData = [];
          this.selectedSahdtData = [];

          const getDataFromMeta = (selector) => {
                const element = document.querySelector(selector);
                if (!element) return [];

                const rawText = element.textContent.replace(/[\n\r]+|[\s]{2,}/g, ' ').trim();

                if (!rawText) return [];

                try {
                    return JSON.parse(rawText);
                } catch (e) {
                    console.error("JSON parse error for selector:", selector, "Raw text:", rawText);
                    return [];
                }
            };
          
          const getDataFromLocalStorage = (key) => {
              const data = localStorage.getItem(key);
              return data ? JSON.parse(data) : [];
          };
          
          if (isCustomer && !isCustomerB2B) {
              this.selectedYMMData = getDataFromMeta('[data-vehile-customer-metafields] [data-vehicle-meta]');
              this.selectedHeavyEqipData = getDataFromMeta('[data-vehile-customer-metafields] [data-hevay-equp-meta]');
              this.selectedSideYMMData = getDataFromMeta('[data-vehile-customer-metafields] [data-side-vehicle-meta]');
              this.selectedTrufData = getDataFromMeta('[data-vehile-customer-metafields] [data-turf-vehicle-meta]');
              this.selectedSahdtData = getDataFromMeta('[data-vehile-customer-metafields] [data-sahdt-vehicle-meta]');
              ['vehicle_data', 'heavy_equipment_data', 'vehicle_data_side', 'turf_data', 'vehicle_data_sahdt'].forEach(key => localStorage.removeItem(key));
          
          } else if (isCustomer && isCustomerB2B) {
              ['vehicle_data', 'heavy_equipment_data', 'vehicle_data_side', 'turf_data', 'vehicle_data_sahdt'].forEach(key => localStorage.removeItem(key));
          
          } else {
              this.selectedYMMData = getDataFromLocalStorage('vehicle_data');
              this.selectedHeavyEqipData = getDataFromLocalStorage('heavy_equipment_data');
              this.selectedSideYMMData = getDataFromLocalStorage('vehicle_data_side');
              this.selectedTrufData = getDataFromLocalStorage('turf_data');
              this.selectedSahdtData = getDataFromLocalStorage('vehicle_data_sahdt');

          }
  
          window.customerSelectedVehicles = {
          'vehicle' : this.selectedYMMData,
          'heavyVehicle' : this.selectedHeavyEqipData,
          'sidebyside': this.selectedSideYMMData,
          'turf': this.selectedTrufData,
          'sahdt': this.selectedSahdtData
          };

          this._normalizeSingleDefault();
      }

      /**
       * Ensure exactly one vehicle across all types has default: true (for header/list checked state).
       * Same logic as VehicleSelectorsUtil.normalizeSingleDefault; keeps YMM and other vehicles consistent.
       */
      _normalizeSingleDefault() {
          const isDefaultTrue = function(v) {
              return v && typeof v === 'object' && (v.default === true || v.default === 'true' || v.default === 1);
          };
          const arrs = [
              this.selectedHeavyEqipData,
              this.selectedYMMData,
              this.selectedTrufData,
              this.selectedSideYMMData,
              this.selectedSahdtData
          ];
          let lastWithTrue = null;
          arrs.forEach(function(arr) {
              if (!Array.isArray(arr)) return;
              arr.forEach(function(v) {
                  if (isDefaultTrue(v)) lastWithTrue = v;
              });
          });
          arrs.forEach(function(arr) {
              if (Array.isArray(arr)) {
                  arr.forEach(function(v) {
                      if (v && typeof v === 'object') v.default = false;
                  });
              }
          });
          if (lastWithTrue) {
              lastWithTrue.default = true;
          } else {
              var all = this._getAllVehicles();
              if (all.length > 0) {
                  all[all.length - 1].default = true;
              }
          }
      }

      /** Combined list of all vehicle arrays in fixed order (heavy, ymm, turf, side, sahdt). */
      _getAllVehicles() {
          return [
              ...(this.selectedHeavyEqipData || []),
              ...(this.selectedYMMData || []),
              ...(this.selectedTrufData || []),
              ...(this.selectedSideYMMData || []),
              ...(this.selectedSahdtData || [])
          ];
      }

      /** Single source for vehicle display label (equipment or year/make/model + trim/cab). */
      _formatVehicleLabel(v) {
          return `${v.equipment_category || v.year} ${v.equipment_manufacturer || v.make} ${v.equipment_model_number || v.model}${(v.trim || v.cabType) ? ', ' + [v.trim, v.cabType].filter(Boolean).join(', ') : ''}`;
      }

      /**
       * Fetch collection by filter.p.tag, parse product cards, update .collection_material and [data-selectmaterialcta].
       * @param {string} partsNumber - e.g. 'sku-1,sku-2'
       * @returns {Promise<void>}
       */
      _updateCollectionPageFromParts(partsNumber) {
          return fetch(`/collections/all?filter.p.tag=${partsNumber}`)
              .then(response => response.text())
              .then(responseText => {
                  const product_Array = [];
                  const html = new DOMParser().parseFromString(responseText, 'text/html');
                  const productCards = html.querySelectorAll('.collection-product');
                  if (productCards.length > 0) {
                      productCards.forEach(card => {
                          product_Array.push({
                              [card.getAttribute('data-productfabric')]: card.getAttribute('data-partnumber')
                          });
                      });
                  }
                  if (product_Array.length > 0) {
                      const result = {};
                      product_Array.forEach(item => {
                          const key = Object.keys(item)[0];
                          const value = `sku-${item[key]}`;
                          if (!result[key]) result[key] = [];
                          if (!result[key].includes(value)) result[key].push(value);
                      });
                      document.querySelectorAll('.collection_material').forEach(el => el.classList.add('d-none'));
                      Object.entries(result).forEach(([key, values]) => {
                          const product_type = key;
                          const product_sku = values.join(',');
                          const matchingElements = document.querySelectorAll(`.collection_material[data-material='${product_type}']`);
                          if (matchingElements.length > 0) {
                              document.querySelectorAll('.collection_material').forEach(el => el.classList.add('d-none'));
                              matchingElements.forEach(element => {
                                  element.classList.remove('d-none');
                                  element.querySelectorAll('[data-selectmaterialcta]').forEach(btn => {
                                      if (btn) {
                                          btn.setAttribute('data-has-products', 'true');
                                          btn.innerHTML = 'Shop your Vehicle';
                                          btn.setAttribute('href', `/collections/all?filter.p.tag=${product_sku}`);
                                          this._hideCollectionLoading();
                                      }
                                  });
                              });
                          } else {
                              document.querySelectorAll('.collection_material').forEach(el => el.classList.remove('d-none'));
                              this._hideCollectionLoading();
                          }
                      });
                  }
              });
      }

      _hideCollectionLoading() {
          const el = document.querySelector('.block__collection-loading');
          if (el) {
              el.classList.add('d-none');
              document.body.classList.remove('overflow-hidden');
          }
          const gridWrapper = document.querySelector('[data-enableGrids]');
          if (gridWrapper) {
              gridWrapper.classList.remove('disabled-grids');
          }
      }

      /** Update filter bar UI with selected vehicle string (used by all collection template cases). */
      _applyCollectionFilterUI(vehicleString) {
          document.querySelectorAll('[data-activefilters]').forEach(el => {
              el.innerHTML = vehicleString || '';
          });
          document.querySelector('[data-selectyym]')?.classList.add('d-none');
          document.querySelector('.filter-box + div')?.classList.remove('d-none');
      }

      /** Persist selection to localStorage and trigger collection page update from parts. */
      _persistSelectionAndUpdateCollection(selectionData, partsNumber) {
          if (!selectionData || !partsNumber) return;
          localStorage.setItem('currentSelectedData', JSON.stringify(selectionData));
          localStorage.setItem('currentSelectedDataLabels', JSON.stringify(selectionData));
          this._updateCollectionPageFromParts(partsNumber).catch(error => {
              console.error('Error updating product details:', error);
          });
      }

      /**
       * Render PDP-style vehicle rows into all elements matching containerSelector.
       * @param {string} containerSelector - e.g. '[data-selected-vehicle]' or '[data-selected-vehicle-list]'
       * @param {string} idSuffix - e.g. '-pdp' or '-list'
       * @param {string} inputName - e.g. 'list-vehicle-pdp' or 'list-vehicle-pdp-list'
       */
      _renderSelectedVehicleContainers(containerSelector, idSuffix, inputName) {
          const containers = document.querySelectorAll(containerSelector);
          if (!containers.length) return;
          const isDefaultChecked = (v) => v && typeof v === 'object' && (v.default === true || v.default === 'true' || v.default === 1);
          const emptyHTML = `<div class="d-flex justify-content-center mb-3 mt-4"><span class="fw-bold text-white">No Vehicle has been added yet.</span></div>`;
          const all = this._getAllVehicles();
          const html = all.length > 0
              ? all.map(v => {
                  const label = this._formatVehicleLabel(v);
                  return `
                    <div class="d-flex justify-content-between vehicle_list-item mb-3" data-vehicle-id="${v.id}">
                        <div class="align-items-center d-flex justify-content-between w-100">
                            <input type="radio" data-list-vehicle-checkbox class="form-check-input me-2 mt-1 d-none" id="vehicle-${v.id}${idSuffix}" name="${inputName}" value="${label}" ${isDefaultChecked(v) ? 'checked' : ''}>
                            <label class="check1 align-items-center d-none" for="vehicle-${v.id}${idSuffix}">
                                <img src="https://cdn.shopify.com/s/files/1/0766/9245/5708/files/icon-check.svg?v=1742361727" class="d-none block__icon-check">
                                <i class="d-flex me-2 icon-truck icon-size-28" style="color: #d7d9db;"></i>
                                <span class="fw-bold lh-lg text-white"> ${label}</span>
                            </label>
                            <div class="block__shopping-text fst-italic fw-bold ls-0 d-none text-gray-300 text-uppercase">currently shopping</div>
                        </div>
                    </div>`;
              }).join('')
              : emptyHTML;
          containers.forEach(el => { el.innerHTML = html; });
      }

      /** Sync from customerSelectedVehicles, build list/mobile/selected HTML, write to DOM. */
      _refreshVehicleLists() {
          const source = window.customerSelectedVehicles;
          if (!source) return;
          this.selectedYMMData = source.vehicle ?? [];
          this.selectedHeavyEqipData = source.heavyVehicle ?? [];
          this.selectedSideYMMData = source.sidebyside ?? [];
          this.selectedTrufData = source.turf ?? [];
          this.selectedSahdtData = source.sahdt ?? [];

          const hasAnyVehicle = this.selectedHeavyEqipData.length > 0 || this.selectedYMMData.length > 0 || this.selectedSideYMMData.length > 0 || this.selectedTrufData.length > 0 || this.selectedSahdtData.length > 0;
          const emptyListHTML = `<div class="d-flex justify-content-center mb-3 mt-4"><span class="fw-bold">No Vehicle has been added yet.</span></div>`;
          const isDefaultChecked = (v) => v && typeof v === 'object' && (v.default === true || v.default === 'true' || v.default === 1);
          const _listItemHTML = (v, idPrefix, inputName) => `
                <div class="d-flex justify-content-between vehicle_list-item mb-3" data-vehicle-id="${v.id}">
                    <div class="d-flex">
                        <input type="radio" data-list-vehicle-checkbox class="form-check-input me-2 mt-1" id="${idPrefix}${v.id}" name="${inputName}" value="${this._formatVehicleLabel(v)}" ${isDefaultChecked(v) ? 'checked' : ''}>
                        <label class="check1 ps-1 align-items-center d-flex" for="${idPrefix}${v.id}">
                            <span class="fw-semibold lh-lg text-black"> ${this._formatVehicleLabel(v)}</span>
                        </label>
                    </div>
                    <a href="#" class="delete_button ms-1 d-inline-block pt-1 text-decoration-none" data-remove-vehicle>
                        <i class="icon-delete text-gray-500 icon-size-17"></i>
                    </a>
                </div>`;

          const all = this._getAllVehicles();
          const listVehicleHTML = hasAnyVehicle ? all.map(v => _listItemHTML(v, 'vehicle-', 'list-vehicle')).join('') : emptyListHTML;
          const listMobileVehicleHTML = hasAnyVehicle ? all.map(v => _listItemHTML(v, 'mobile-vehicle-', 'mobile-list-vehicle')).join('') : emptyListHTML;

          this._renderSelectedVehicleContainers('[data-selected-vehicle]', '-pdp', 'list-vehicle-pdp');
          this._renderSelectedVehicleContainers('[data-selected-vehicle-list]', '-list', 'list-vehicle-pdp-list');

          const saveEle = document.querySelector('[data-my-save-vehicle]');
          if (saveEle) saveEle.innerHTML = listVehicleHTML;
          const mobileEle = document.querySelector('[data-my-mobile-save-vehicle]');
          if (mobileEle) mobileEle.innerHTML = listMobileVehicleHTML;

          document.dispatchEvent(new CustomEvent('vehicle-list-refreshed'));
      }

      // List vehicle in my vehicle modal (refresh lists + collection template state)
      // When opts.skipCollectionPageUpdate is true, only refresh list UI; do not update collection products
      // (used when loading on visibility so products change only when the user changes the vehicle)
      async _listVehicle(opts) {
          this._hideCollectionLoading();
          this._refreshVehicleLists();
          if (opts && opts.skipCollectionPageUpdate) return;
          if (!document.querySelectorAll('[data-activefilters]').length) return;

          const collection_template = window.globalVariables?.templateSuffix;
          if (collection_template === 'material') await this.getOrLoad('vehicle');
          else if (collection_template === 'side-by-side') {
              await this.getOrLoad('side-by-side');
              await this.getOrLoad('semis-heavy-duty-trucks');
          } else if (collection_template === 'equipment-seat-cover') await this.getOrLoad('heavy-equipment');
          else if (collection_template === 'agri-turf') await this.getOrLoad('agri-turf');

        function getUrlParameter(name) {
            name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
            var regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
                results = regex.exec(location.search);
            return results === null ? '' : decodeURIComponent(results[1]);
        }

        const filterActive = getUrlParameter('filter.p.tag');
        setTimeout(() => {
            if (filterActive !== '') {
                this._hideCollectionLoading();
                return;
            }
            const collection_template = window.globalVariables?.templateSuffix;
            let selectedData = localStorage.getItem('currentSelectedDataLabels');
            if (!selectedData || selectedData === 'undefined' || selectedData.trim() === '' || selectedData === '{"id":"","year":"","make":"","model":"","trim":"","cabType":""}') {
                selectedData = localStorage.getItem('currentSelectedData');
            }
            let current_vehicle_id;
            if (selectedData != null) {
                try {
                    const current_vehicle_json = JSON.parse(selectedData);
                    current_vehicle_id = current_vehicle_json.id;
                } catch (_) {}
            }

            switch (collection_template) {
                case 'material':
                        if (this.selectedYMMData.length > 0) {
                            let ST_selectedyear, ST_selectedmake, ST_selectedmodel, ST_selectedtrim, idToUpdate;
                            const currentYMMData = this.selectedYMMData.find(vehicle => vehicle.id == current_vehicle_id);

                            let vehicleObj = currentYMMData || this.selectedYMMData[0];

                            ST_selectedyear = vehicleObj.year || "";
                            ST_selectedmake = vehicleObj.make || "";
                            ST_selectedmodel = vehicleObj.model || "";
                            ST_selectedtrim = vehicleObj.trim || "";
                            idToUpdate = vehicleObj.id || "";

                            let vehicleString = `${ST_selectedyear} ${ST_selectedmake} ${ST_selectedmodel} ${ST_selectedtrim}`;
                            const vehicleElement = document.querySelector(`.topbar [data-my-save-vehicle] [data-vehicle-id='${idToUpdate}']`);
                            if (vehicleElement) {
                                const checkbox = vehicleElement.querySelector('[data-list-vehicle-checkbox]');
                                if (checkbox) vehicleString = checkbox.value;
                            }
                            this._applyCollectionFilterUI(vehicleString);

                            const isYMMVehicle = this.selectedYMMData.some(vehicle => vehicle.id == idToUpdate);

                            // Reset default flags
                            this.selectedYMMData.forEach(vehicle => vehicle.default = false);

                            if (isYMMVehicle) {
                                let selectedYearData = {
                                    id: "",
                                    year: "",
                                    make: "",
                                    model: "",
                                    trim: "",
                                    cabType: ""
                                };

                                const matchVehicle = this.selectedYMMData.find(v => v.id == idToUpdate);
                                if (matchVehicle) {
                                    selectedYearData = {
                                        id: matchVehicle.id,
                                        year: matchVehicle.year || "",
                                        make: matchVehicle.make || "",
                                        model: matchVehicle.model || "",
                                        trim: matchVehicle.trim || "",
                                        cabType: matchVehicle.cabType || "",
                                        default: true
                                    };
                                }

                                // Normalize values
                                const safeSelectedYear = (selectedYearData.year || "").toLowerCase();
                                const safeSelectedMake = (selectedYearData.make || "").toLowerCase();
                                const safeSelectedModel = (selectedYearData.model || "").toLowerCase();
                                const safeSelectedTrim = (selectedYearData.trim || "").toLowerCase();
                                const safeSelectedCabType = (selectedYearData.cabType || "").toLowerCase();

                                const vehicleData = Object.values(window.globalVariables?.vehicleJson ?? {});
                                this.uniqueRows = [...new Set(vehicleData.map(item => item.row))];

                                let filteredRows = [];

                                // Step 1: Filter rows
                                if (safeSelectedTrim === '' && safeSelectedCabType === '') {
                                    if (safeSelectedModel !== "") {
                                        filteredRows = [...new Set(vehicleData.filter(item =>
                                            (item.year || "").toLowerCase() === safeSelectedYear &&
                                            (item.make || "").toLowerCase() === safeSelectedMake &&
                                            (item.model || "").toLowerCase() === safeSelectedModel
                                        ).map(item => item.row))];
                                    } else {
                                        filteredRows = this.uniqueRows;
                                    }
                                } else if (safeSelectedTrim !== '' && safeSelectedCabType === '') {
                                    filteredRows = [...new Set(vehicleData.filter(item =>
                                        (item.year || "").toLowerCase() === safeSelectedYear &&
                                        (item.make || "").toLowerCase() === safeSelectedMake &&
                                        (item.model || "").toLowerCase() === safeSelectedModel &&
                                        (item.trim || "").toLowerCase() === safeSelectedTrim
                                    ).map(item => item.row))];
                                } else {
                                    filteredRows = [...new Set(vehicleData.filter(item =>
                                        (item.year || "").toLowerCase() === safeSelectedYear &&
                                        (item.make || "").toLowerCase() === safeSelectedMake &&
                                        (item.model || "").toLowerCase() === safeSelectedModel &&
                                        (item.trim || "").toLowerCase() === safeSelectedTrim &&
                                        ((item.cab_type || item.cabType || "").toLowerCase()) === safeSelectedCabType
                                    ).map(item => item.row))];
                                }

                                // Step 2: Get part numbers
                                let uniquePartNumbers = [];

                                filteredRows.forEach(row => {
                                    let filteredParts;

                                    if (safeSelectedTrim === '' && safeSelectedCabType === '') {
                                        filteredParts = [...new Set(vehicleData.filter(item =>
                                            (item.year || "").toLowerCase() === safeSelectedYear &&
                                            (item.make || "").toLowerCase() === safeSelectedMake &&
                                            (item.model || "").toLowerCase() === safeSelectedModel &&
                                            item.row === row
                                        ).map(item => item.part_number))];
                                    } else if (safeSelectedTrim !== '' && safeSelectedCabType === '') {
                                        filteredParts = [...new Set(vehicleData.filter(item =>
                                            (item.year || "").toLowerCase() === safeSelectedYear &&
                                            (item.make || "").toLowerCase() === safeSelectedMake &&
                                            (item.model || "").toLowerCase() === safeSelectedModel &&
                                            (item.trim || "").toLowerCase() === safeSelectedTrim &&
                                            item.row === row
                                        ).map(item => item.part_number))];
                                    } else {
                                        filteredParts = [...new Set(vehicleData.filter(item =>
                                            (item.year || "").toLowerCase() === safeSelectedYear &&
                                            (item.make || "").toLowerCase() === safeSelectedMake &&
                                            (item.model || "").toLowerCase() === safeSelectedModel &&
                                            (item.trim || "").toLowerCase() === safeSelectedTrim &&
                                            ((item.cab_type || item.cabType || "").toLowerCase()) === safeSelectedCabType &&
                                            item.row === row
                                        ).map(item => item.part_number))];
                                    }

                                    uniquePartNumbers.push(...filteredParts);
                                });

                                // Step 3: Generate final partsNumber
                                const prefixedParts = uniquePartNumbers.map(p => `sku-${p}`);
                                const partsNumber = prefixedParts.join(",");

                                if (vehicleElement) {
                                    this._persistSelectionAndUpdateCollection(selectedYearData, partsNumber);
                                }
                            }
                        }else{
                            this._hideCollectionLoading();
                        }
                    break;
                case 'side-by-side':
                        if(document.querySelectorAll(".side-by-side-seat-covers").length > 0){
                            if (this.selectedSideYMMData.length > 0) {
                                // Step 1: Extract selected vehicle
                                const selectedVehicle = this.selectedSideYMMData.find(v => v.id == current_vehicle_id) || this.selectedSideYMMData[0];

                                const selectedYear = selectedVehicle.year || "";
                                const selectedMake = selectedVehicle.make || "";
                                const selectedModel = selectedVehicle.model || "";
                                const idToUpdate = selectedVehicle.id || "";

                                let vehicleString = `${selectedYear} ${selectedMake} ${selectedModel}`;
                                const vehicleElement = document.querySelector(`.topbar [data-my-save-vehicle] [data-vehicle-id='${idToUpdate}']`);
                                const checkbox = vehicleElement?.querySelector('[data-list-vehicle-checkbox]');
                                if (checkbox?.value) vehicleString = checkbox.value;
                                this._applyCollectionFilterUI(vehicleString);

                                // console.log("idToUpdate", idToUpdate);

                                // Step 4: Check and mark default vehicle
                                const isSSVehicle = this.selectedSideYMMData.some(vehicle => vehicle.id == idToUpdate);
                                if (isSSVehicle) {
                                    // Reset default flags
                                    this.selectedSideYMMData.forEach(v => v.default = false);

                                    const matchedVehicle = this.selectedSideYMMData.find(v => v.id == idToUpdate);
                                    matchedVehicle.default = true;

                                    const selectedSSYearData = {
                                        id: matchedVehicle.id,
                                        year: matchedVehicle.year || "",
                                        make: matchedVehicle.make || "",
                                        model: matchedVehicle.model || ""
                                    };

                                    // Step 5: Filter parts
                                    const filteredParts = [
                                        ...new Set(
                                            Object.values(window.globalVariables?.sidevehicleJson ?? {})
                                                .filter(item =>
                                                    item.year == selectedSSYearData.year &&
                                                    item.make == selectedSSYearData.make &&
                                                    item.model == selectedSSYearData.model
                                                )
                                                .map(item => item.part_number)
                                        )
                                    ];

                                    const prefixedParts = filteredParts.map(p => `sku-${p}`);
                                    const partsNumber = prefixedParts.join(',');
                                    this._persistSelectionAndUpdateCollection(selectedSSYearData, partsNumber);
                                }
                            } else {
                                this._hideCollectionLoading();
                            }
                        } else if (this.selectedSahdtData.length > 0) {
                            const currentVehicle = this.selectedSahdtData.find(v => v.id == current_vehicle_id) || this.selectedSahdtData[0];

                            const {
                                year: ST_selectedyear = "",
                                make: ST_selectedmake = "",
                                model: ST_selectedmodel = "",
                                id: idToUpdate = ""
                            } = currentVehicle;

                            const vehicleElement = document.querySelector(`.topbar [data-my-save-vehicle] [data-vehicle-id='${idToUpdate}']`);
                            let vehicleString = vehicleElement?.querySelector('[data-list-vehicle-checkbox]')?.value || `${ST_selectedyear} ${ST_selectedmake} ${ST_selectedmodel}`;
                            this._applyCollectionFilterUI(vehicleString);

                            const isSahdtVehicle = this.selectedSahdtData.some(v => v.id == idToUpdate);
                            this.selectedSahdtData.forEach(v => v.default = v.id == idToUpdate);

                            if (isSahdtVehicle) {
                                const sahdtData = {
                                    id: idToUpdate,
                                    year: ST_selectedyear,
                                    make: ST_selectedmake,
                                    model: ST_selectedmodel
                                };

                                // Filter parts
                                const filteredParts = [
                                    ...new Set(
                                        Object.values(window.globalVariables?.sahdtJson ?? {})
                                            .filter(item =>
                                                item.year == sahdtData.year &&
                                                item.make == sahdtData.make &&
                                                item.model == sahdtData.model
                                            )
                                            .map(item => item.part_number)
                                    )
                                ];

                                const partsNumber = filteredParts.map(p => `sku-${p}`).join(',');
                                this._persistSelectionAndUpdateCollection(sahdtData, partsNumber);
                            }
                        } else {
                            this._hideCollectionLoading();
                        }
                    break;
                case 'equipment-seat-cover':
                        if (this.selectedHeavyEqipData.length > 0) {
                            // Step 1: Get selected or fallback vehicle
                            const vehicle = this.selectedHeavyEqipData.find(v => v.id == current_vehicle_id) || this.selectedHeavyEqipData[0];

                            const selectedEqipType = vehicle.equipment_category || "";
                            const selectedEqipManufacturer = vehicle.equipment_manufacturer || "";
                            const selectedEqipModel = vehicle.equipment_model_number || "";
                            const idToUpdate = vehicle.id || "";

                            const vehicleElement = document.querySelector(`.topbar [data-my-save-vehicle] [data-vehicle-id='${idToUpdate}']`);
                            let vehicleString = vehicleElement?.querySelector('[data-list-vehicle-checkbox]')?.value || `${selectedEqipType} ${selectedEqipManufacturer} ${selectedEqipModel}`;
                            this._applyCollectionFilterUI(vehicleString);

                            const isHeavyVehicle = this.selectedHeavyEqipData.some(v => v.id == idToUpdate);
                            const isSahdtVehicle = this.selectedSahdtData.some(v => v.id == idToUpdate);

                            if (isHeavyVehicle && !isSahdtVehicle) {
                                // Step 4: Mark default vehicle and prepare structured data
                                this.selectedHeavyEqipData.forEach(v => v.default = v.id == idToUpdate);

                                const selectedEquipData = {
                                    id: idToUpdate,
                                    equipment_category: selectedEqipType,
                                    equipment_manufacturer: selectedEqipManufacturer,
                                    equipment_model_number: selectedEqipModel
                                };

                                // Step 5: Filter parts from heavyvehicleJson
                                const filteredParts = [
                                    ...new Set(
                                        Object.values(window.globalVariables?.heavyvehicleJson ?? {}).filter(item =>
                                            item.equipment_category == selectedEquipData.equipment_category &&
                                            item.equipment_manufacturer == selectedEquipData.equipment_manufacturer &&
                                            item.equipment_model_number == selectedEquipData.equipment_model_number
                                        ).map(item => item.equipment_part_number)
                                    )
                                ];

                                const partsNumber = filteredParts.map(p => `sku-${p}`).join(',');
                                this._persistSelectionAndUpdateCollection(selectedEquipData, partsNumber);
                            }
                        } else {
                            this._hideCollectionLoading();
                        }
                    break;
                case 'agri-turf':
                        if (this.selectedTrufData.length > 0) {
                            // Get current or fallback vehicle
                            const vehicle = this.selectedTrufData.find(v => v.id == current_vehicle_id) || this.selectedTrufData[0];
                            const { equipment_category = "", equipment_manufacturer = "", equipment_model_number = "", id = "" } = vehicle;


                            const selectedEqipType = vehicle.equipment_category || "";
                            const selectedEqipManufacturer = vehicle.equipment_manufacturer || "";
                            const selectedEqipModel = vehicle.equipment_model_number || "";
                            const idToUpdate = vehicle.id || "";

                            const vehicleElement = document.querySelector(`.topbar [data-my-save-vehicle] [data-vehicle-id='${id}']`);
                            const vehicleString = vehicleElement?.querySelector('[data-list-vehicle-checkbox]')?.value?.trim() || `${equipment_category} ${equipment_manufacturer} ${equipment_model_number}`;
                            this._applyCollectionFilterUI(vehicleString);

                            const isTurfVehicle = this.selectedTrufData.some(v => v.id == id);
                            const isSahdtVehicle = this.selectedSahdtData.some(v => v.id == id);

                            const selectedagriturfData = {
                                id: idToUpdate,
                                equipment_category: selectedEqipType,
                                equipment_manufacturer: selectedEqipManufacturer,
                                equipment_model_number: selectedEqipModel
                            };

                            if (isTurfVehicle && !isSahdtVehicle) {
                                // Mark selected vehicle as default
                                this.selectedTrufData.forEach(v => v.default = v.id == id);

                                // Filter matching parts
                                const filteredParts = [
                                    ...new Set(
                                        Object.values(window.globalVariables?.agriTurfVehicleJson ?? {}).filter(item =>
                                            item.equipment_category === equipment_category &&
                                            item.equipment_manufacturer === equipment_manufacturer &&
                                            item.equipment_model_number === equipment_model_number
                                        ).map(item => item.equipment_part_number)
                                    )
                                ];

                                const partsNumber = filteredParts.map(p => `sku-${p}`).join(',');
                                this._persistSelectionAndUpdateCollection(selectedagriturfData, partsNumber);
                            }
                        } else {
                            this._hideCollectionLoading();
                        }
                    break;
                default:
                    this._hideCollectionLoading();
            }
        }, 200);
        
      }
  
      // List account vehicles
      async _listAccountVehicle(){
        
        let accountVehicleHTML = '';
        if (this.selectedHeavyEqipData.length > 0 || this.selectedYMMData.length > 0 || this.selectedSideYMMData.length > 0 || this.selectedTrufData.length > 0 || this.selectedSahdtData.length > 0) {
    
            const vehicleDataArrays = [
                { data: this.selectedYMMData, type: 'YMM' },
                { data: this.selectedHeavyEqipData, type: 'HeavyEqip' },
                { data: this.selectedTrufData, type: 'Truf' },
                { data: this.selectedSideYMMData, type: 'SideYMM' },
                { data: this.selectedSahdtData, type: 'sahdt' },
            ];
            
            const isDefaultVehicle = (v) => v && typeof v === 'object' && (v.default === true || v.default === 'true' || v.default === 1);
            const createVehicleHTMLAcc = (selectedVehicle, type) => {
                const isYMM = type === 'YMM' || type === 'SideYMM' || type == 'sahdt' ;
                const details = isYMM
                    ? `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}`
                    : `${selectedVehicle.equipment_category} ${selectedVehicle.equipment_manufacturer} ${selectedVehicle.equipment_model_number}`;
                const additionalInfo = selectedVehicle.trim ? `, ${selectedVehicle.trim}, ${selectedVehicle.cabType}` : '';
                const isDefault = isDefaultVehicle(selectedVehicle);

                return `
                <div class="vehicle-item col-12 px-lg-3 px-md-2 px-2" data-vehicle-id="${selectedVehicle.id}">
                    <div class="border border-grey d-flex flex-column h-100 vehicle-card ${isDefault ? 'bg-gray-100' : ''}">
                        <div class="align-items-center d-block d-sm-flex d-md-block d-lg-flex justify-content-between vehicle-card-body">
                            <div>
                            <div class="align-items-center d-flex vehicle-item-checkbox">
                                <input type="radio" data-list-vehicle-checkbox class="form-check-input me-2 mt-0" id="account-vehicle-${selectedVehicle.id}" name="account-list-vehicle" ${isDefault ? 'checked' : ''}>
                                <label for="account-vehicle-${selectedVehicle.id}" class="ps-1 fw-semibold">${isDefault ? 'Currently Shopping For:' : 'Set as current vehicle'}</label>
                            </div>
                            <div class="ps-4 vehicle-name mt-3 ms-1">
                                <h5 class="fw-semibold ${isYMM ? ` mb-2` : 'mb-0'} text-uppercase">${details}</h5>
                                ${isYMM ? `<p class="mb-0 lh-lg pe-xl-5">${details}${additionalInfo}</p>` : ''}
                            </div>
                            <div class="align-items-center d-flex justify-content-start mt-auto vehicle-card-footer">
                                <a href="#" class="fw-semibold lh-lg me-2" data-edit-vehicle data-modal-opener="change-vehicle">Edit Vehicle</a>|
                                <a href="#" class="align-items-center d-flex text-gray-700 ms-2" data-remove-vehicle>Delete</a>
                            </div>
                            </div>
                            <div class="me-xl-3 ms-5 ms-sm-0 ms-md-5 ms-xl-0 mt-4 mt-sm-0 mt-md-4 mt-xl-0 pe-xl-1">
                            <a href="javascript:void(0);" class="btn btn-primary btn-sm" data-shop-vehiclebtn>Shop this vehicle</a>
                            </div>
                        </div>
                    </div>
                </div>`;
            };
    
            vehicleDataArrays.forEach(vehicleArray => {
                vehicleArray.data.forEach(selectedVehicle => {
                    // Create HTML for each vehicle and append it to accountVehicleHTML
                    accountVehicleHTML += createVehicleHTMLAcc(selectedVehicle, vehicleArray.type);
                });
            });
    
        } else {
            accountVehicleHTML = `
            <div class="d-flex justify-content-center mb-3 mt-4">
                <span class="fw-bold">No Vehicle has been added yet.</span>
            </div>`;
        }
  
        let myAccountVehicleList = document.querySelector('[data-vehicle-list-item]');
        myAccountVehicleList.innerHTML = accountVehicleHTML;
      }
  }

  var vehicleUtility = new VehicleUtilities();
  window.vehicleUtility = vehicleUtility;

  // Delegated list handlers so Edit/Remove/Change default work before CSV loads and after list refresh
  (function () {
    if (vehicleUtility._listVehicleDelegationAttached) return;
    vehicleUtility._listVehicleDelegationAttached = true;

    document.addEventListener('change', function (event) {
      if (!event.target.matches('[data-list-vehicle-checkbox]')) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      var el = event.target.closest('shop-vehicle') || document.querySelector('shop-vehicle[data-account-vehicle-handler]') || document.querySelector('shop-vehicle[data-global-vehicle-handler]') || document.querySelector('shop-vehicle');
      if (el && typeof el._updateDefault === 'function') el._updateDefault(event.target);
    });

    document.addEventListener('click', function (event) {
      var target = event.target.closest ? event.target.closest('[data-remove-vehicle], [data-edit-vehicle], [data-shop-vehiclebtn], [data-clear-vehicle-data]') : null;
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      var el = target.closest('shop-vehicle') || document.querySelector('shop-vehicle[data-account-vehicle-handler]') || document.querySelector('shop-vehicle[data-global-vehicle-handler]') || document.querySelector('shop-vehicle');
      if (!el) return;
      if (target.matches('[data-remove-vehicle]')) {
        if (typeof el._removeVehicle === 'function') el._removeVehicle(target);
      } else if (target.matches('[data-edit-vehicle]')) {
        if (typeof el._handleEditVehicleClick === 'function') el._handleEditVehicleClick(target);
      } else if (target.matches('[data-shop-vehiclebtn]')) {
        var checkbox = target.closest('.vehicle-item')?.querySelector('[data-list-vehicle-checkbox]');
        if (checkbox) checkbox.click();
      } else if (target.matches('[data-clear-vehicle-data]')) {
        if (typeof el._clearVehicleData === 'function') el._clearVehicleData();
      }
    }, true);
  })();

  // Run collection vehicle list / filter on page load when this is a collection page with active filters
  (function runCollectionListVehicleOnLoad() {
    function run() {
      if (typeof vehicleUtility === 'undefined') return;
      if (!document.querySelectorAll('[data-activefilters]').length) return;
      if (!window.customerSelectedVehicles) vehicleUtility._deterMineVehicles();
      vehicleUtility._listVehicle();
    }
    function schedule() {
      setTimeout(run, 150);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', schedule);
    } else {
      schedule();
    }
  })();

  // On every page load: for logged-in customers, determine vehicles from DOM metafields and refresh header list
  // so "My Vehicle" in the top bar shows the correct default after redirect (e.g. from YMM list selection).
  (function initHeaderVehicleListOnLoad() {
    function run() {
      if (typeof vehicleUtility === 'undefined') return;
      var isCustomer = document.body.classList.contains('customer-loggedin');
      var isCustomerB2B = document.body.classList.contains('b2b-customer');
      if (!isCustomer || isCustomerB2B) return;
      if (!document.querySelector('[data-my-save-vehicle]')) return;
      vehicleUtility._deterMineVehicles();
      vehicleUtility._refreshVehicleLists();
    }
    function schedule() {
      setTimeout(run, 100);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', schedule);
    } else {
      schedule();
    }
  })();